import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, Card, InputAdornment, Skeleton, Stack, TextField, Typography } from '@mui/material';

import settingsApi from '../../../api/settingsApi';
import { getErrorMessage } from '../../../utils/errorHandler';

const isValidRate = (value) => value !== '' && !Number.isNaN(Number(value)) && Number(value) > 0;

const rateSlotProps = {
  htmlInput: { step: '0.0001', min: 0 },
  input: {
    startAdornment: <InputAdornment position="start">S/</InputAdornment>,
    endAdornment: <InputAdornment position="end">por US$ 1.00</InputAdornment>,
  },
};

// Tipos de cambio USD -> PEN: el general, con el que se deriva el precio en soles de cada
// producto, y el del rol distribuidor, que solo ve y compra en soles (ver ExchangeRateController
// y Product::pricePenDistribuidor() en la API).
export default function ExchangeRateSettingsPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rate, setRate] = useState('');
  const [distributorRate, setDistributorRate] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    settingsApi
      .getExchangeRate()
      .then(({ data }) => {
        setRate(String(data.data.exchange_rate));
        setDistributorRate(String(data.data.exchange_rate_distribuidor));
      })
      .catch((err) => enqueueSnackbar(getErrorMessage(err, 'No se pudo cargar el tipo de cambio.'), { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  const handleSave = async () => {
    const nextErrors = {};
    if (!isValidRate(rate)) nextErrors.rate = 'Ingresa un tipo de cambio válido.';
    if (!isValidRate(distributorRate)) nextErrors.distributorRate = 'Ingresa un tipo de cambio válido.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    try {
      const { data } = await settingsApi.updateExchangeRate(Number(rate), Number(distributorRate));
      setRate(String(data.data.exchange_rate));
      setDistributorRate(String(data.data.exchange_rate_distribuidor));
      enqueueSnackbar('Tipo de cambio actualizado correctamente.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err, 'No se pudo actualizar el tipo de cambio.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4">Configuración</Typography>
        <Typography variant="body2" color="text.secondary">
          Tipos de cambio de referencia (S/ por US$ 1.00).
        </Typography>
      </div>

      <Card sx={{ p: 3, maxWidth: 420 }}>
        {loading ? (
          <Stack spacing={2.5}>
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={56} />
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <TextField
              label="Tipo de cambio"
              type="number"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              error={Boolean(errors.rate)}
              helperText={errors.rate || 'Precios en soles de la tienda, cotizaciones y ventas.'}
              slotProps={rateSlotProps}
            />

            <TextField
              label="Tipo de cambio distribuidor"
              type="number"
              value={distributorRate}
              onChange={(e) => setDistributorRate(e.target.value)}
              error={Boolean(errors.distributorRate)}
              helperText={
                errors.distributorRate || 'Solo para los precios del rol distribuidor, que se muestran y cobran en soles.'
              }
              slotProps={rateSlotProps}
            />

            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </Stack>
        )}
      </Card>
    </Stack>
  );
}
